import { useState } from 'react'
import { useNavigate, Link } from 'react-router-dom'
import { supabaseAuth } from '@/lib/supabase-auth'

export default function RegisterPage() {
  const [step, setStep] = useState<'email' | 'verify'>('email')
  const [name, setName] = useState('')
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [confirmPassword, setConfirmPassword] = useState('')
  const [verificationCode, setVerificationCode] = useState('')
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState('')
  const [success, setSuccess] = useState(false)
  const navigate = useNavigate()

  // 密码复杂度验证
  const validatePassword = (pwd: string): string | null => {
    if (pwd.length < 8) {
      return '密码至少需要 8 个字符'
    }
    if (!/[A-Z]/.test(pwd)) {
      return '密码必须包含至少一个大写字母'
    }
    if (!/[a-z]/.test(pwd)) {
      return '密码必须包含至少一个小写字母'
    }
    if (!/[0-9]/.test(pwd)) {
      return '密码必须包含至少一个数字'
    }
    return null
  }

  const handleSendCode = async (e: React.FormEvent) => {
    e.preventDefault()
    setError('')
    setLoading(true)

    try {
      // 验证
      if (!name.trim()) {
        throw new Error('请输入姓名')
      }

      if (!email.trim()) {
        throw new Error('请输入邮箱')
      }

      // 验证密码
      const passwordError = validatePassword(password)
      if (passwordError) {
        throw new Error(passwordError)
      }

      if (password !== confirmPassword) {
        throw new Error('两次输入的密码不一致')
      }

      // 发送 OTP 验证码到邮箱
      const { error: otpError } = await supabaseAuth.auth.signInWithOtp({
        email,
        options: {
          data: {
            name,
            user_type: 'admin',
          },
        },
      })

      if (otpError) throw otpError

      setStep('verify')
    } catch (err) {
      setError(err instanceof Error ? err.message : '发送验证码失败')
    } finally {
      setLoading(false)
    }
  }

  const handleVerify = async (e: React.FormEvent) => {
    e.preventDefault()
    setError('')
    setLoading(true)

    try {
      if (!verificationCode.trim()) {
        throw new Error('请输入验证码')
      }

      // 验证 OTP 代码
      const { data: otpData, error: verifyError } = await supabaseAuth.auth.verifyOtp({
        email,
        token: verificationCode,
        type: 'email',
      })

      if (verifyError) throw verifyError

      // OTP 验证成功后，先登出（因为 verifyOtp 会自动登录）
      await supabaseAuth.auth.signOut()

      // 创建带密码的账户
      const { data: signUpData, error: signUpError } = await supabaseAuth.auth.signUp({
        email,
        password,
        options: {
          emailRedirectTo: `${window.location.origin}/login`,
          data: {
            name,
            user_type: 'admin',
          },
        },
      })

      if (signUpError) throw signUpError

      if (signUpData.user) {
        // 创建管理员记录
        const response = await fetch('/api/admin/auth/register', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            auth_user_id: signUpData.user.id,
            email,
            name,
          }),
        })

        if (!response.ok) {
          const errorData = await response.json()
          throw new Error(errorData.error || '创建管理员记录失败')
        }

        setSuccess(true)
        setTimeout(() => {
          navigate('/login')
        }, 2000)
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : '验证失败')
    } finally {
      setLoading(false)
    }
  }

  if (success) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-blue-50 flex items-center justify-center p-4">
        <div className="w-full max-w-md">
          <div className="bg-white rounded-2xl shadow-xl border border-gray-100 p-8 text-center">
            <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <svg
                className="w-8 h-8 text-green-600"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M5 13l4 4L19 7"
                />
              </svg>
            </div>
            <h2 className="text-2xl font-bold text-gray-900 mb-2">注册成功！</h2>
            <p className="text-gray-600 mb-4">
              管理员账号已创建
            </p>
            <p className="text-sm text-gray-500 mb-6">
              您已成功验证邮箱并创建管理员账号，现在可以登录使用。
            </p>
            <p className="text-sm text-gray-400">2 秒后自动跳转到登录页...</p>
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-blue-50 flex items-center justify-center p-4">
      <div className="w-full max-w-md">
        {/* Logo and Title */}
        <div className="text-center mb-8">
          <div className="inline-flex items-center justify-center mb-4">
            <img
              src="https://flowspark.net/favicon.ico"
              alt="Logo"
              className="w-16 h-16 rounded-2xl shadow-lg"
            />
          </div>
          <h1 className="text-3xl font-bold bg-gradient-to-r from-blue-600 to-indigo-600 bg-clip-text text-transparent mb-2">
            API Adapter
          </h1>
          <p className="text-gray-600">创建管理员账号</p>
        </div>

        {/* Register Form */}
        <div className="bg-white rounded-2xl shadow-xl border border-gray-100 p-8">
          <h2 className="text-2xl font-semibold text-gray-900 mb-2">注册管理员</h2>
          <p className="text-sm text-gray-600 mb-6">
            {step === 'email' ? '我们将向您的邮箱发送验证码' : '请输入邮箱中收到的验证码'}
          </p>

          {error && (
            <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-xl text-red-700 text-sm">
              {error}
            </div>
          )}

          {step === 'email' ? (
            <form onSubmit={handleSendCode} className="space-y-5">
              <div>
                <label
                  htmlFor="name"
                  className="block text-sm font-semibold text-gray-700 mb-2"
                >
                  姓名
                </label>
                <input
                  id="name"
                  type="text"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all text-base"
                  placeholder="请输入姓名"
                  required
                />
              </div>

              <div>
                <label
                  htmlFor="email"
                  className="block text-sm font-semibold text-gray-700 mb-2"
                >
                  邮箱地址
                </label>
                <input
                  id="email"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all text-base"
                  placeholder="admin@example.com"
                  required
                />
              </div>

              <div>
                <label
                  htmlFor="password"
                  className="block text-sm font-semibold text-gray-700 mb-2"
                >
                  密码
                </label>
                <input
                  id="password"
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all text-base"
                  placeholder="至少8位，包含大小写字母和数字"
                  required
                />
                <p className="text-xs text-gray-500 mt-1">
                  密码要求：至少8位，包含大写字母、小写字母和数字
                </p>
              </div>

              <div>
                <label
                  htmlFor="confirmPassword"
                  className="block text-sm font-semibold text-gray-700 mb-2"
                >
                  确认密码
                </label>
                <input
                  id="confirmPassword"
                  type="password"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all text-base"
                  placeholder="再次输入密码"
                  required
                />
              </div>

              <button
                type="submit"
                disabled={loading}
                className="w-full py-3 px-4 bg-gradient-to-r from-blue-600 to-blue-700 hover:from-blue-700 hover:to-blue-800 text-white font-semibold rounded-xl shadow-lg hover:shadow-xl transition-all disabled:opacity-50 disabled:cursor-not-allowed text-base"
              >
                {loading ? '发送中...' : '发送验证码'}
              </button>
            </form>
          ) : (
            <form onSubmit={handleVerify} className="space-y-5">
              <div className="p-4 bg-blue-50 border border-blue-200 rounded-xl">
                <p className="text-sm text-blue-800">
                  验证码已发送到 <span className="font-semibold">{email}</span>
                </p>
                <p className="text-xs text-blue-600 mt-1">
                  请检查您的邮箱（包括垃圾邮件文件夹）
                </p>
              </div>

              <div>
                <label
                  htmlFor="code"
                  className="block text-sm font-semibold text-gray-700 mb-2"
                >
                  验证码
                </label>
                <input
                  id="code"
                  type="text"
                  value={verificationCode}
                  onChange={(e) => setVerificationCode(e.target.value)}
                  className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all text-base text-center text-2xl tracking-widest"
                  placeholder="000000"
                  required
                  maxLength={6}
                  autoComplete="off"
                />
                <p className="text-xs text-gray-500 mt-2">请输入 6 位验证码</p>
              </div>

              <button
                type="submit"
                disabled={loading}
                className="w-full py-3 px-4 bg-gradient-to-r from-blue-600 to-blue-700 hover:from-blue-700 hover:to-blue-800 text-white font-semibold rounded-xl shadow-lg hover:shadow-xl transition-all disabled:opacity-50 disabled:cursor-not-allowed text-base"
              >
                {loading ? '验证中...' : '验证并注册'}
              </button>

              <button
                type="button"
                onClick={() => setStep('email')}
                className="w-full py-2 text-sm text-gray-600 hover:text-gray-900 transition-colors"
              >
                返回修改邮箱
              </button>
            </form>
          )}

          <div className="mt-6 pt-6 border-t border-gray-200">
            <p className="text-center text-sm text-gray-600">
              已有账号？{' '}
              <Link to="/login" className="text-blue-600 hover:text-blue-700 font-medium">
                立即登录
              </Link>
            </p>
          </div>
        </div>

        <p className="text-center text-xs text-gray-500 mt-8">
          © 2025 API Adapter. All rights reserved.
        </p>
      </div>
    </div>
  )
}
